import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Calendar, Plus, Trash2, Clock, AlertCircle, CheckCircle, BookOpen, CalendarPlus, SkipForward } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, differenceInDays, differenceInHours, differenceInMinutes } from 'date-fns';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StudySchedulePage = () => {
  const [exams, setExams] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [studyEvents, setStudyEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('events');
  const [formData, setFormData] = useState({ subject_id: '', title: '', exam_date: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [examsRes, subjectsRes, eventsRes] = await Promise.all([
        axios.get(`${API}/exams`),
        axios.get(`${API}/subjects`),
        axios.get(`${API}/schedule/events`).catch(() => ({ data: [] }))
      ]);
      setExams(examsRes.data);
      setSubjects(subjectsRes.data);
      setStudyEvents(eventsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/exams`, {
        ...formData,
        exam_date: new Date(formData.exam_date).toISOString()
      });
      toast.success('Exam scheduled!');
      setOpen(false);
      setFormData({ subject_id: '', title: '', exam_date: '' });
      fetchData();
    } catch (error) {
      console.error('Error creating exam:', error);
      toast.error('Failed to schedule exam');
    } finally {
      setSubmitting(false);
    }
  };

  const getCountdown = (examDate) => {
    const now = new Date();
    const exam = new Date(examDate);
    const days = differenceInDays(exam, now);
    const hours = differenceInHours(exam, now) % 24;
    const minutes = differenceInMinutes(exam, now) % 60;

    if (days < 0) return { text: 'Past', color: 'text-gray-500', urgent: false };
    if (days === 0 && hours < 1) return { text: `${minutes}m`, color: 'text-red-600', urgent: true };
    if (days === 0) return { text: `${hours}h ${minutes}m`, color: 'text-red-600', urgent: true };
    if (days === 1) return { text: 'Tomorrow', color: 'text-orange-600', urgent: true };
    if (days <= 3) return { text: `${days} days`, color: 'text-orange-600', urgent: true };
    if (days <= 7) return { text: `${days} days`, color: 'text-yellow-600', urgent: false };
    return { text: `${days} days`, color: 'text-primary', urgent: false };
  };

  const upcomingExams = exams.filter(e => !e.completed && new Date(e.exam_date) >= new Date()).sort((a, b) => new Date(a.exam_date) - new Date(b.exam_date));
  const pastExams = exams.filter(e => e.completed || new Date(e.exam_date) < new Date()).sort((a, b) => new Date(b.exam_date) - new Date(a.exam_date));

  const pendingEvents = studyEvents.filter(e => e.status === 'pending').sort((a, b) => a.date.localeCompare(b.date));
  const completedEvents = studyEvents.filter(e => e.status === 'completed');

  const handleEventStatus = async (eventId, status) => {
    try {
      await axios.put(`${API}/schedule/events/${eventId}/status?status=${status}`);
      toast.success(status === 'completed' ? 'Session marked complete!' : 'Session skipped');
      fetchData();
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      await axios.delete(`${API}/schedule/events/${eventId}`);
      toast.success('Event removed');
      fetchData();
    } catch {
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96">Loading...</div>;
  }

  return (
    <div className="f500-page space-y-6 sm:space-y-8" data-testid="study-schedule-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4 border-b border-[var(--f500-line)] pb-6">
        <div>
          <p className="f500-eyebrow mb-2">— Schedule</p>
          <h1 className="f500-display text-3xl sm:text-4xl">Plan it. Beat it.</h1>
          <p className="f500-body text-base mt-2">Schedule exams, track study sessions, and stay ahead of every deadline.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="f500-btn f500-btn-primary w-full sm:w-auto" data-testid="add-exam-button">
              <Plus className="w-4 h-4" />
              Schedule Exam
            </button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md  bg-slate-900 border-[var(--isp-glass-border)] text-white" data-testid="exam-dialog">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-[var(--isp-heading)]">Schedule New Exam</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="subject_id" className="text-[var(--isp-body)]">Subject *</Label>
                <Select
                  value={formData.subject_id}
                  onValueChange={(value) => setFormData({ ...formData, subject_id: value })}
                  required
                >
                  <SelectTrigger className="rounded-xl bg-slate-800 border-[var(--isp-glass-border)] text-white" data-testid="exam-subject-select">
                    <SelectValue placeholder="Select subject" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-[var(--isp-glass-border)]">
                    {subjects.map((subject) => (
                      <SelectItem key={subject.id} value={subject.id} className="text-[var(--isp-heading)]">
                        {subject.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="title" className="text-[var(--isp-body)]">Exam Title *</Label>
                <Input
                  id="title"
                  placeholder="e.g., Midterm Exam"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="rounded-xl bg-slate-800 border-[var(--isp-glass-border)] text-white"
                  data-testid="exam-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="exam_date" className="text-[var(--isp-body)]">Exam Date & Time *</Label>
                <Input
                  id="exam_date"
                  type="datetime-local"
                  value={formData.exam_date}
                  onChange={(e) => setFormData({ ...formData, exam_date: e.target.value })}
                  required
                  className="rounded-xl bg-slate-800 border-[var(--isp-glass-border)] text-white"
                  data-testid="exam-date-input"
                />
              </div>
              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setOpen(false)} className="flex-1 rounded-xl border-[var(--isp-glass-border)] text-[var(--isp-heading)]" data-testid="cancel-exam-button">Cancel</Button>
                <Button type="submit" disabled={submitting || !formData.subject_id} className="flex-1 rounded-xl bg-[#002147] hover:bg-[#00142A]" data-testid="submit-exam-button">
                  {submitting ? 'Scheduling...' : 'Schedule'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[var(--isp-glass-border)] pb-1">
        <button
          onClick={() => setActiveTab('events')}
          className={`px-4 py-2 text-sm font-medium rounded-t-xl transition-all ${
            activeTab === 'events'
              ? 'bg-[var(--isp-glass-inner)] text-[#FFD700] border-b-2 border-[#FFD700]'
              : 'text-[var(--isp-body)] hover:text-[var(--isp-heading)]'
          }`}
          data-testid="tab-study-events"
        >
          <CalendarPlus className="w-4 h-4 inline mr-2" />
          Study Sessions ({pendingEvents.length})
        </button>
        <button
          onClick={() => setActiveTab('exams')}
          className={`px-4 py-2 text-sm font-medium rounded-t-xl transition-all ${
            activeTab === 'exams'
              ? 'bg-[var(--isp-glass-inner)] text-[#FFD700] border-b-2 border-[#FFD700]'
              : 'text-[var(--isp-body)] hover:text-[var(--isp-heading)]'
          }`}
          data-testid="tab-exams"
        >
          <Calendar className="w-4 h-4 inline mr-2" />
          Exams ({upcomingExams.length})
        </button>
      </div>

      {/* Study Events Tab */}
      <AnimatePresence mode="wait">
        {activeTab === 'events' && (
          <motion.div key="events" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            {pendingEvents.length === 0 ? (
              <Card className="p-12  isp-card text-center" data-testid="no-events">
                <CalendarPlus className="w-14 h-14 mx-auto mb-4 text-[var(--isp-muted)]" />
                <h3 className="text-xl font-medium text-[var(--isp-heading)] mb-2">No study sessions scheduled</h3>
                <p className="text-[var(--isp-body)] mb-4 text-sm">Upload a syllabus in Document Uploads, analyze it, then click "Schedule" to auto-generate sessions.</p>
              </Card>
            ) : (
              <div className="space-y-2">
                {pendingEvents.map((event, index) => {
                  const isToday = event.date === new Date().toISOString().split('T')[0];
                  const isPast = event.date < new Date().toISOString().split('T')[0];
                  return (
                    <motion.div
                      key={event.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.03 }}
                      className={`flex items-center gap-4 p-4 rounded-xl isp-card border-l-4 ${
                        isToday ? 'border-l-[#FFD700]' : isPast ? 'border-l-red-400' : 'border-l-violet-500'
                      }`}
                      data-testid={`event-${event.id}`}
                    >
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex flex-col items-center justify-center flex-shrink-0">
                        <span className="text-[10px] font-bold text-white/70">{event.date ? format(new Date(event.date + 'T00:00:00'), 'MMM') : ''}</span>
                        <span className="text-base font-bold text-white leading-none">{event.date ? format(new Date(event.date + 'T00:00:00'), 'd') : ''}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-[var(--isp-heading)] truncate">{event.topic}</p>
                        <p className="text-xs text-[var(--isp-body)] mt-0.5 truncate">{event.task}</p>
                        <div className="flex items-center gap-3 mt-1 text-[10px] text-[var(--isp-muted)]">
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{event.duration_minutes || 60}min</span>
                          {event.exam_title && <span className="flex items-center gap-1"><BookOpen className="w-3 h-3" />{event.exam_title}</span>}
                          {isToday && <span className="text-[#FFD700] font-semibold">TODAY</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button size="sm" onClick={() => handleEventStatus(event.id, 'completed')} className="rounded-xl gap-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`complete-${event.id}`}>
                          <CheckCircle className="w-3 h-3" /> Done
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleEventStatus(event.id, 'skipped')} className="rounded-xl text-xs border-[var(--isp-glass-border)] text-[var(--isp-body)]" data-testid={`skip-${event.id}`}>
                          <SkipForward className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteEvent(event.id)} className="rounded-xl text-red-400 hover:text-red-300" data-testid={`del-event-${event.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}

            {completedEvents.length > 0 && (
              <div className="mt-6">
                <h3 className="text-sm font-medium text-[var(--isp-muted)] mb-3">Completed ({completedEvents.length})</h3>
                <div className="space-y-1.5 opacity-60">
                  {completedEvents.slice(0, 5).map((event) => (
                    <div key={event.id} className="flex items-center gap-3 p-3 rounded-xl isp-card">
                      <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                      <span className="text-sm text-[var(--isp-body)] truncate">{event.topic}</span>
                      <span className="text-xs text-[var(--isp-muted)] ml-auto">{event.date}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Exams Tab */}
        {activeTab === 'exams' && (
          <motion.div key="exams" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div>
              <h2 className="text-2xl font-medium text-[var(--isp-heading)] mb-4">Upcoming Exams</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {upcomingExams.length === 0 ? (
                  <Card className="col-span-full p-12  isp-card text-center">
                    <Calendar className="w-16 h-16 mx-auto mb-4 text-[var(--isp-muted)] opacity-30" />
                    <h3 className="text-xl font-medium text-[var(--isp-heading)] mb-2">No upcoming exams</h3>
                    <p className="text-[var(--isp-body)] mb-6">Schedule your first exam to start planning</p>
                    <Button onClick={() => setOpen(true)} className="rounded-xl bg-[#002147] hover:bg-[#00142A] text-white" data-testid="empty-add-exam">
                      <Plus className="w-4 h-4 mr-2" />
                      Schedule Exam
                    </Button>
                  </Card>
                ) : (
                  upcomingExams.map((exam, index) => {
                    const subject = subjects.find(s => s.id === exam.subject_id);
                    const countdown = getCountdown(exam.exam_date);
                    return (
                      <motion.div key={exam.id} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3, delay: index * 0.1 }}>
                        <Card
                          className={`p-6  isp-card border-2 hover:shadow-lg transition-all ${
                            countdown.urgent ? 'border-red-500 bg-red-500/5' : 'border-[var(--isp-glass-border)]'
                          }`}
                          data-testid={`exam-card-${exam.id}`}
                        >
                          {countdown.urgent && (
                            <div className="flex items-center gap-2 mb-3 text-red-500">
                              <AlertCircle className="w-4 h-4" />
                              <span className="text-xs font-medium uppercase">Urgent!</span>
                            </div>
                          )}
                          {subject && (
                            <div className="inline-block px-2 py-1 rounded-lg text-xs font-medium text-white mb-3" style={{ backgroundColor: subject.color }}>
                              {subject.name}
                            </div>
                          )}
                          <h3 className="text-lg font-bold text-[var(--isp-heading)] mb-2">{exam.title}</h3>
                          <div className="flex items-center gap-2 text-sm text-[var(--isp-body)] mb-4">
                            <Calendar className="w-4 h-4" />
                            {format(new Date(exam.exam_date), 'PPp')}
                          </div>
                          <div className="flex items-center justify-between pt-4 border-t border-[var(--isp-glass-border)]">
                            <div className="flex items-center gap-2">
                              <Clock className={`w-5 h-5 ${countdown.color}`} />
                              <span className={`text-2xl font-bold ${countdown.color}`}>{countdown.text}</span>
                            </div>
                          </div>
                        </Card>
                      </motion.div>
                    );
                  })
                )}
              </div>
            </div>

            {pastExams.length > 0 && (
              <div className="mt-8">
                <h2 className="text-2xl font-medium text-[var(--isp-heading)] mb-4">Past Exams</h2>
                <div className="space-y-3">
                  {pastExams.map((exam) => {
                    const subject = subjects.find(s => s.id === exam.subject_id);
                    return (
                      <Card key={exam.id} className="p-4  isp-card opacity-60" data-testid={`past-exam-${exam.id}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            {subject && (
                              <span className="inline-block px-2 py-1 rounded-lg text-xs font-medium text-white mb-2" style={{ backgroundColor: subject.color }}>
                                {subject.name}
                              </span>
                            )}
                            <h3 className="font-medium text-[var(--isp-heading)]">{exam.title}</h3>
                            <p className="text-sm text-[var(--isp-body)]">{format(new Date(exam.exam_date), 'PPP')}</p>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StudySchedulePage;